import { useEffect, useCallback, useRef } from 'react';
import { updateAutoPath } from '../AutoPathRenderer';
import { updateCustomPath } from '../CustomPathRenderer';
import { updateLocations } from '../MapMarkers';
import { 
  AUTO_PATH_LAYER, 
  CUSTOM_PATH_LAYER,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  POINTS_SOURCE
} from '../constants';

// Unified path management hook
export const useUnifiedPathManager = ({
  map,
  mapReady,
  sourcesReadyRef,
  isMountedRef,
  
  // Path data
  autoPath,
  customPath,
  locations,
  startId,
  
  // Display controls
  showAutoPath,
  showCustomPath
}) => {
  // Use ref to track last render state, avoid unnecessary re-renders
  const lastRenderState = useRef({
    autoPath: null,
    locations: null,
    visibility: {
      showAutoPath: false,
      showCustomPath: false
    }
  });

  const shouldUpdatePath = useCallback((pathType, newData, oldData) => {
    if (!newData && !oldData) return false;
    if (!newData || !oldData) return true;
    
    // For location data, force update if we have data but map source is empty
    if (pathType === 'locations' && newData && newData.length > 0) {
      try {
        if (map.current && map.current.getSource(POINTS_SOURCE)) {
          const currentSourceData = map.current.getSource(POINTS_SOURCE)._data;
          if (!currentSourceData || !currentSourceData.features || currentSourceData.features.length === 0) {
            return true; // Force update - critical fix for location visibility
          }
        }
      } catch (e) {
        console.warn('Error checking map source data:', e);
      }
    }
    
    // Simple deep comparison for path data
    try {
      return JSON.stringify(newData) !== JSON.stringify(oldData);
    } catch {
      // If serialization fails, default to update
      return true;
    }
  }, [map]);

  // Unified layer visibility management
  const manageLayerVisibility = useCallback(() => {
    if (!map.current || !mapReady || !sourcesReadyRef.current) {
      return;
    }

    try {
      // Auto path layer visibility
      if (map.current.getLayer(AUTO_PATH_LAYER)) {
        map.current.setLayoutProperty(
          AUTO_PATH_LAYER,
          'visibility',
          showAutoPath ? 'visible' : 'none'
        );
      }

      // Custom path layer visibility
      if (map.current.getLayer(CUSTOM_PATH_LAYER)) {
        map.current.setLayoutProperty(
          CUSTOM_PATH_LAYER,
          'visibility',
          showCustomPath ? 'visible' : 'none'
        );
      }



      // Critical fix: Ensure location layers are always visible
      // Location layers should be displayed in all modes
      const ensurePointLayersVisible = () => {
        if (map.current.getLayer(POINTS_CIRCLE_LAYER)) {
          map.current.setLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility', 'visible');
        } else {
          // Retry if layer not found (timing issue)
          setTimeout(() => {
            if (map.current && map.current.getLayer(POINTS_CIRCLE_LAYER)) {
              map.current.setLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility', 'visible');
            }
          }, 100);
        }

        if (map.current.getLayer(POINTS_SYMBOL_LAYER)) {
          map.current.setLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility', 'visible');
        } else {
          // Retry if layer not found (timing issue)
          setTimeout(() => {
            if (map.current && map.current.getLayer(POINTS_SYMBOL_LAYER)) {
              map.current.setLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility', 'visible');
            }
          }, 100);
        }
      };

      ensurePointLayersVisible();

      // Update visibility state
      lastRenderState.current.visibility = {
        showAutoPath,
        showCustomPath
      };

    } catch (error) {
      console.error('Error managing layer visibility:', error);
    }
  }, [map, mapReady, sourcesReadyRef, showAutoPath, showCustomPath]);

  // Main path management Effect
  useEffect(() => {
    if (!isMountedRef.current || !map.current || !mapReady || !sourcesReadyRef.current) {
      return;
    }

    // Processing path updates

    try {
      // 1. Update location points (highest priority - other paths depend on locations)
      if (shouldUpdatePath('locations', locations, lastRenderState.current.locations)) {
        updateLocations({
          map: map.current,
          mapReady,
          sourcesReady: sourcesReadyRef.current,
          locations,
          startId
        });
        lastRenderState.current.locations = locations;
      }

      // 2. Update auto path
      if (showAutoPath && autoPath) {
        if (shouldUpdatePath('auto', autoPath, lastRenderState.current.autoPath)) {
          updateAutoPath({
            map: map.current,
            mapReady,
            sourcesReady: sourcesReadyRef.current,
            autoPath,
            locations,
            shouldShowAutoPath: true
          });
          lastRenderState.current.autoPath = autoPath;
        }
      } else if (lastRenderState.current.autoPath) {
        // Clear auto path
        updateAutoPath({
          map: map.current,
          mapReady,
          sourcesReady: sourcesReadyRef.current,
          autoPath: null,
          locations,
          shouldShowAutoPath: false
        });
        lastRenderState.current.autoPath = null;
      }

      // 2.5. Update custom path (simple path array format)
      if (showCustomPath && customPath && Array.isArray(customPath)) {
        updateCustomPath({
          map: map.current,
          mapReady,
          sourcesReady: sourcesReadyRef.current,
          customPath,
          locations,
          shouldShowCustomPath: true
        });
      } else if (!showCustomPath || !customPath) {
        // Clear custom path when not showing or no path
        updateCustomPath({
          map: map.current,
          mapReady,
          sourcesReady: sourcesReadyRef.current,
          customPath: [],
          locations,
          shouldShowCustomPath: false
        });
      }



    } catch (error) {
      console.error('Error in path management:', error);
    }

  }, [
    // Core dependencies
    map, mapReady, sourcesReadyRef, isMountedRef,
    
    // Data dependencies
    autoPath, locations, startId,
    
    // Display control dependencies
    showAutoPath, showCustomPath,
    
    // Helper functions
    shouldUpdatePath
  ]);

  // Layer visibility management Effect (independent of data updates)
  useEffect(() => {
    manageLayerVisibility();
  }, [manageLayerVisibility]);

  // Return useful state information (optional)
  return {
    isReady: mapReady && sourcesReadyRef.current,
    hasAutoPath: !!lastRenderState.current.autoPath,
    lastUpdate: lastRenderState.current
  };
};
